import { useState, useCallback, useMemo } from "react";
import {
  TimelineFilters,
  TimelineSortOptions,
  type TimelineItem,
} from "@/lib/hooks/api/timeline/enhanced-timeline";

export const useTimelineState = () => {
  const [filters, setFilters] = useState<TimelineFilters>({});
  const [sort, setSort] = useState<TimelineSortOptions>({
    field: "occurred_at",
    direction: "desc",
  });
  const [viewMode, setViewMode] = useState<"list" | "compact">("list");
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedTimelineItem, setSelectedTimelineItem] = useState<TimelineItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [limit, setLimit] = useState<number>(30);

  // Memoized event handlers
  const handleOpenModal = useCallback(() => setIsLogModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsLogModalOpen(false), []);
  const handleToggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === "list" ? "compact" : "list"));
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);
  const handleLoadMore = useCallback(() => {
    setLimit((prev) => prev + 25);
  }, []);

  // Computed values
  const computedValues = useMemo(
    () => ({
      hasFilters: Object.keys(filters).length > 0,
    }),
    [filters]
  );

  return {
    // State
    filters,
    sort,
    viewMode,
    isLogModalOpen,
    selectedTimelineItem,
    isDetailModalOpen,
    limit,

    // Setters
    setFilters,
    setSort,
    setViewMode,
    setIsLogModalOpen,
    setSelectedTimelineItem,
    setIsDetailModalOpen,
    setLimit,

    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleToggleViewMode,
    handleClearFilters,
    handleLoadMore,

    // Computed
    ...computedValues,
  };
};
